import type { Activity, ActivityType, Venue, VenueCategory } from '../types';

/**
 * Turning a saved place into a stop on a day.
 *
 * Shared by the two paths that do it — the per-venue modal and the bulk action
 * in the Places list — so a place scheduled one at a time and a place scheduled
 * in a batch land as the same card. The mapping used to be copied into each
 * caller, which is how a place added in bulk could have ended up typed OTHER
 * while the same place added singly came out RESTAURANT.
 */

const CAT_TO_TYPE: Partial<Record<VenueCategory, ActivityType>> = {
  RESTAURANT: 'RESTAURANT', CAFE: 'CAFE', ATTRACTION: 'MONUMENT',
  VIEWPOINT: 'VIEWPOINT', NEIGHBORHOOD: 'NEIGHBORHOOD', PARK: 'PARK',
  SHOP: 'SHOP', EXPERIENCE: 'EXPERIENCE', TRANSPORT_HUB: 'TRANSPORT',
  BEACH: 'BEACH', NATURE: 'NATURE', PARKING: 'PARKING', OTHER: 'OTHER',
};

export const activityTypeForVenue = (category: VenueCategory): ActivityType =>
  CAT_TO_TYPE[category] ?? 'OTHER';

export function venueActivityPayload(venue: Venue): Partial<Activity> {
  return {
    title:     venue.name,
    type:      activityTypeForVenue(venue.category),
    // ADR-0020: a place of the user's own kind becomes a stop of that same kind.
    customType: venue.category === 'OTHER' ? venue.customCategory : null,
    venueName: venue.name,
    zone:      venue.zone ?? undefined,
    address:   venue.address ?? undefined,
    venueId:   venue.id,
    placeId:   venue.placeId ?? undefined,
  };
}

/**
 * An activity is "this venue" when it links by id, or (for activities created
 * before venueId existed, and for imported ones) when the names match. Origin
 * markers copy the last stop's venueId and name but are managed automatically,
 * so they never count as an assignment the user made.
 */
export function activitiesForVenue(activities: Activity[], venue: Venue): Activity[] {
  const name = venue.name.toLowerCase();
  return activities.filter(a =>
    !a.isOrigin && (
      a.venueId === venue.id ||
      a.venueName?.toLowerCase() === name ||
      a.title.toLowerCase() === name
    ),
  );
}
